import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { slotKey } from '../signing-refs.js';
import { signMeasureToken, useSignMeasure, useSignIncludePlaceDate, useSignDigitalStatement, } from '../signing-measurement.js';
/**
 * The two signing statements rendered once above the signature panels
 * (opt-in via `SigningBlock`'s `statement` prop). Which one shows is
 * coupled to the same `SignIncludePlaceDateContext` that gates the
 * Ort/Datum rows — the choice is a single toggle, not two:
 *
 *   - PHYSICAL (place/date ON): the document is printed and signed by
 *     hand in one original per party, so the parties write place + date
 *     on the line.
 *   - DIGITAL (place/date OFF): the document is e-signed; BankID stamps
 *     the signing date into the watermark, so no place/date blanks.
 *
 * The digital wording is Scrive-only (`SignDigitalStatementContext`):
 * outside Scrive, place/date OFF renders no statement.
 *
 * Copy supplied by André (legal). The physical statement's copy count is
 * computed from the number of signing parties (one original each), so a
 * 3-party agreement reads "tre likalydande exemplar", not "två".
 */
export const SIGNING_STATEMENT_DIGITAL = 'Avtalet har upprättats och signerats digitalt, varpå parterna har tagit var sitt digitalt exemplar; datum för signering framgår av vattenstämpeln.';
const SWEDISH_CARDINALS = [
    'noll', 'ett', 'två', 'tre', 'fyra', 'fem', 'sex', 'sju', 'åtta', 'nio', 'tio',
];
/** Swedish cardinal for small counts; digit fallback past the table. */
function swedishCardinal(n) {
    return SWEDISH_CARDINALS[n] ?? String(n);
}
/**
 * Physical signing statement for `partyCount` parties — one likalydande
 * original per party. Falls back to "två" for a degenerate <2 count
 * (a signed agreement always has at least two parties).
 */
export function signingStatementPhysical(partyCount) {
    const word = swedishCardinal(partyCount >= 2 ? partyCount : 2);
    return `Avtalet har upprättats i ${word} likalydande exemplar, varvid parterna har tagit var sitt original.`;
}
/**
 * Per-party signing panel.
 *
 * Renders one panel per *signer* with up to four labeled rows:
 *   - Underskrift — {label}       (the actual signature line / image)
 *   - Namnförtydligande — {label} (printed name)
 *   - Ort                         (place of signing — optional, no suffix)
 *   - Datum                       (date of signing — optional, no suffix)
 *
 * The Ort/Datum rows are gated by `SignIncludePlaceDateContext` (ACT-06):
 * shown by default (preview + manual signing), hidden when a caller opts
 * out (the Scrive send flow defaults them off). They carry no party
 * suffix — the panel above already identifies the party, and the blanks
 * are filled in by hand, so the bare "Ort"/"Datum" labels read cleaner.
 *
 * The compound `"<Field> — <PartyLabel>"` label is the load-bearing
 * detail: it makes each row's anchor text unique across the document,
 * so Scrive can position fields by anchor text (no x/y guessing, no
 * `index:N` disambiguation) AND the rendered PDF is self-documenting
 * for the manual-signing path (someone receiving a printout knows
 * exactly which line is theirs).
 *
 * When a `signatories` roster is supplied (at `mode: 'final'` render —
 * signing time, per ADR-0044) a party label expands into one panel per
 * signer, and a per-signer discriminator is appended to every anchor so
 * the strings stay unique with N signers. Without a roster (draft /
 * preview / gallery, or a party with no listed signers) it renders a
 * single panel per label with NO discriminator — byte-identical to the
 * pre-roster behaviour, so single-signer placements are unchanged.
 *
 * The block intentionally has NO dependency on signing mode. The same
 * PDF works as:
 *   - a Scrive-signed document — `buildSigningPlacementsForTemplate()`
 *     overlays each signer's signature image, typed place, and
 *     Scrive-stamped sign date using these anchors. It derives the same
 *     discriminator from the same roster, so render and placements can
 *     never drift.
 *   - a manually-signed document — someone prints it, signs on the
 *     line, and writes ort/datum into the blanks by hand.
 *
 * `parties` are the signing refs (printed label + slot `(role, ordinal)`).
 * Each printed label must be unique within the contract — that uniqueness
 * is what gives every anchor string its uniqueness. The roster is looked
 * up per ref by `slotKey(role, ordinal)`, so the printed label is free to
 * differ from the editor-facing slot label.
 */
export function SigningBlock(props) {
    const includePlaceDate = useSignIncludePlaceDate();
    const digitalStatement = useSignDigitalStatement();
    const statementText = !props.statement
        ? null
        : includePlaceDate
            ? signingStatementPhysical(props.parties.length)
            : digitalStatement
                ? SIGNING_STATEMENT_DIGITAL
                : null;
    return (_jsxs("div", { className: "mt-10", style: { breakInside: 'avoid' }, children: [statementText ? (_jsx("p", { className: "mb-5 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: statementText })) : null, props.hideHeading ? null : (_jsx("div", { className: "mb-3 text-[11px] font-bold", children: "Underskrifter" })), _jsx("div", { className: "grid grid-cols-2 gap-x-8 gap-y-8", children: props.parties.flatMap((p) => {
                    const signers = props.signatories?.[slotKey(p.role, p.ordinal)];
                    if (!signers || signers.length === 0) {
                        return [
                            _jsx(PartySigningPanel, { label: p.label, role: p.role, ordinal: p.ordinal, signerOrdinal: 0, discriminator: "", name: null, entity: null, capacity: null, personalNumber: null }, p.label),
                        ];
                    }
                    return signers.map((s) => (_jsx(PartySigningPanel, { label: p.label, role: p.role, ordinal: p.ordinal, signerOrdinal: s.ordinal, discriminator: signerDiscriminator(s.ordinal, signers.length), name: s.name ?? null, entity: s.entity ?? null, capacity: s.capacity ?? null, personalNumber: s.personalNumber ?? null }, `${p.label}#${s.ordinal}`)));
                }) })] }));
}
function MeasureMark(props) {
    // Black so it reliably lands in the PDF text layer (invisible text can be
    // dropped on print/flatten), and `position: absolute` so it has zero
    // layout impact — the clean render without it has identical geometry, so
    // measured coordinates stay valid. Only ever rendered in the throwaway
    // measurement pass (per ADR-0044); never in the document sent to Scrive.
    return (_jsx("span", { "aria-hidden": true, style: {
            position: 'absolute',
            left: 0,
            bottom: 0,
            margin: 0,
            padding: 0,
            fontSize: '5px',
            lineHeight: '5px',
            color: '#000',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
        }, children: props.token }));
}
function PartySigningPanel(props) {
    const { label, role, ordinal, signerOrdinal, discriminator, name, entity, capacity, personalNumber, } = props;
    const measure = useSignMeasure();
    const includePlaceDate = useSignIncludePlaceDate();
    const mark = (field) => measure ? _jsx(MeasureMark, { token: signMeasureToken(role, ordinal, signerOrdinal, field) }) : null;
    return (_jsxs("div", { className: "text-[10px] text-stone-700", style: { breakInside: 'avoid' }, children: [_jsxs("div", { className: "mb-6", children: [_jsx("div", { className: "border-b border-stone-600", style: { height: '34px', position: 'relative' }, "aria-hidden": true, children: mark('signature') }), _jsx("div", { className: "mt-1 font-sans text-[8.5px] text-stone-500", children: anchorFor(label, 'signature', discriminator) }), name ? (_jsxs("div", { className: "mt-1 text-[10px] text-stone-800", children: ["Namnf\u00F6rtydligande: ", name] })) : null, personalNumber ? (_jsxs("div", { className: "text-[10px] text-stone-700", children: ["Personnummer: ", personalNumber] })) : null, entity ? (_jsx("div", { className: "text-[10px] text-stone-700", children: entity })) : null, capacity ? (_jsx("div", { className: "mt-0.5 text-[9px] italic text-stone-600", children: capacity })) : null] }), name ? null : _jsx(Row, { label: PREFIX.name }), includePlaceDate ? (_jsxs(_Fragment, { children: [_jsx(Row, { label: PREFIX.place, mark: mark('place') }), _jsx(Row, { label: PREFIX.date, mark: mark('date') })] })) : null] }));
}
function Row(props) {
    return (_jsxs("div", { className: "mt-1 grid items-end gap-x-2", style: { gridTemplateColumns: 'auto 1fr' }, children: [_jsxs("div", { className: "whitespace-nowrap text-stone-600", children: [props.label, ":"] }), _jsx("div", { className: "border-b border-stone-400", style: { height: '14px', position: 'relative' }, children: props.mark ?? null })] }));
}
const PREFIX = {
    signature: 'Underskrift',
    name: 'Namnförtydligande',
    place: 'Ort',
    date: 'Datum',
};
export function anchorFor(partyLabel, role, discriminator = '') {
    return `${PREFIX[role]} — ${partyLabel}${discriminator}`;
}
/**
 * The anchor discriminator suffix for one signer within a party.
 *
 * Empty when the party has a single signer — so the common case (and
 * every contract signed before the roster existed) keeps anchor strings
 * exactly as they were. `" (N)"` (1-based) once a party fans out to
 * multiple signers. The render and the placement builder MUST compute
 * this from the same signer list to stay in sync.
 */
export function signerDiscriminator(signerOrdinal, totalSigners) {
    return totalSigners <= 1 ? '' : ` (${signerOrdinal + 1})`;
}
export function signingAnchorsFor(partyLabel, discriminator = '') {
    return {
        signature: anchorFor(partyLabel, 'signature', discriminator),
        name: anchorFor(partyLabel, 'name', discriminator),
        place: anchorFor(partyLabel, 'place', discriminator),
        date: anchorFor(partyLabel, 'date', discriminator),
    };
}
//# sourceMappingURL=SigningBlock.js.map